<template>
    <div class="cart-wrapper">
        <div
            @click="cardOpen = true"
            class="mini-cart"
            :class="{ close: cardOpen, gone: !products.length}"
        >
            <p class="price">
                {{ complete ? 'Jouw bestelling' : 'Winkelmandje' }} ({{ total + 50 | currency }})
            </p>
            <div class="button button-icon">
                <span class="counter">
                    {{ products.length }}
                </span>
                <icon icon="shopping-bag"/>
            </div>
        </div>

        <div
            class="cart"
            :class="{ open: cardOpen }"
        >
            <div class="cart-header d-flex align-items-center justify-content-between">
                <h2>{{ complete ? 'Jouw bestelling' : 'Winkelmandje' }}</h2>
                <button
                    @click="cardOpen = false"
                    class="button button-icon close"
                >
                    <icon icon="x"/>
                </button>
            </div>
            <p
                v-if="!products.length"
                class="cart-empty"
            >
                <i>Kies wat lekkers uit het menu en plaats je winkelmandje.</i>
            </p>
            <p
                v-else
                class="cart-amount"
            >
                Je hebt {{ products.length }} gerecht(en) in jouw {{ complete ? 'bestelling' : 'mandje' }}
            </p>

            <CheckoutOrderType
                :disabled="disableSwitch"
            />

            <ul class="cart-product-list">
                <transition-group name="list">
                    <li
                        v-for="product in products"
                        :key="product.id"
                        class="cart-product-item"
                    >
                        <div class="cart-product-summary">
                            <p class="cart-product-title">
                                {{ product.name }}
                            </p>
                            <div
                                v-if="product.dish_extras && product.dish_extras.length > 0"
                                class="mb-2"
                            >
                                <p
                                    v-for="(productExtra, index) in product.dish_extras"
                                    :key="index"
                                >
                                    <i>+ {{ productExtra.name }}</i>
                                </p>
                            </div>
                            <p class="cart-product-qty">
                                {{ product.quantity }} x
                                <button
                                    v-if="!complete && !checkout"
                                    @click="substractFromCart(product)"
                                    class="button button-icon"
                                >
                                    <icon icon="minus" />
                                </button>
                                <button
                                    v-if="!complete && !checkout"
                                    @click="addOneToCart(product)"
                                    class="button button-icon"
                                >
                                    <icon icon="plus" />
                                </button>
                            </p>
                        </div>
                        <div class="cart-product-price">
                            {{ productPrice(product) | currency }}

                            <button
                                v-if="!complete && !checkout"
                                @click="removeFromCart(product)"
                                class="button button-icon"
                            >
                                <icon icon="trash-2"/>
                            </button>
                        </div>
                    </li>
                </transition-group>
            </ul>

            <div class="cart-total mt-auto">
                <div v-if="products.length">
                    <p class="cart-total-row">
                        <span class="cart-total-label">Subtotaal</span>
                        <span class="cart-total-price"> {{ total | currency }}</span>
                    </p>
                    <p v-if="onlinePaymentFee > 0" class="cart-total-row">
                        <span class="cart-total-label">Online betalen</span>
                        <span class="cart-total-price"> {{ onlinePaymentFee | currency }}</span>
                    </p>
                    <p v-if="this.orderType === 'deliver'" class="cart-total-row">
                        <span class="cart-total-label">Bezorgkosten</span>
                        <span v-if="this.restaurantSettings.shipping_costs <= 0" class="cart-total-price">Gratis</span>
                        <span v-else class="cart-total-price"> {{ restaurantSettings.shipping_costs | currency }}</span>
                    </p>
                    <p class="cart-total-row last">
                        <span class="cart-total-label">Totaal</span>
                        <span class="cart-total-price"> {{ totalPrice | currency }}</span>
                    </p>
                </div>
            </div>
            <div class="cart-checkout">
                <div>
                    <p
                        v-if="!restaurantHasOpeningHours && !complete"
                        class="alert alert-info"
                    >
                        <icon icon="clock"/>
                        {{ restaurant.name }} heeft nog geen openingstijden ingesteld. Bestellen is daarom nog niet mogelijk. Probeer het later nog eens.
                    </p>
                    <p
                        v-else-if="!isRestaurantOpen && !complete"
                        class="alert alert-info"
                    >
                        <icon icon="clock"/>
                        {{ restaurant.name }} is op dit moment gesloten. Probeer het later nog eens.
                    </p>
                </div>


                <div v-if="products.length">
                    <p
                        v-if="showMinimumDeliveryMessage"
                        class="alert alert-warning"
                    >
                        {{ restaurant.name }} hanteert een minimum bestelbedrag van: {{ restaurantSettings.minimum_delivery | currency }} (excl. bezorgkosten)
                    </p>

                    <p
                        v-if="!canOrderToday"
                        class="alert alert-warning"
                    >
                        Bestellen voor vandaag is niet meer mogelijk
                    </p>

                    <button
                        v-if="!checkout && enableCheckoutButton"
                        @click="setCheckout(true); cardOpen = false"
                        :disabled="!products.length"
                        :class="[
                            {
                                disabled: false,
                            },
                            'button cart-checkout-button'
                        ]"
                    >
                        Bestelling afronden
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

    import Icon from '@/components/ui/Icon';
    import CheckoutOrderType from '@/components/checkout/OrderType';

    export default {
        components: {
            Icon,
            CheckoutOrderType,
        },
        props: {
            complete: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                cardOpen: false,
                onlinePaymentFee: 0,
            };
        },
        computed: {
            ...mapState({
                restaurant: state => state.products.restaurant,
                restaurantSettings: state => state.products.settings,
                products: state => state.cart.items,
                checkout: state => state.cart.checkout,
                orderType: state => state.cart.orderType,
            }),
            ...mapGetters({
                canOrderToday: 'cart/canOrderToday',
                isRestaurantOpen: 'products/isRestaurantOpen',
                restaurantHasOpeningHours: 'products/restaurantHasOpeningHours',
                total: 'cart/cartTotalPrice',
            }),

            disablePayment() {
                return !this.canOrderToday
                    && this.checkout;
            },

            totalPrice() {
                if (this.orderType === 'deliver') return (this.total + this.onlinePaymentFee) + this.restaurantSettings.shipping_costs;
                return this.total + this.onlinePaymentFee;
            },

            enableCheckoutButton() {
                return !this.showMinimumDeliveryMessage
                    && this.isRestaurantOpen
                    && this.restaurantHasOpeningHours;
            },

            showMinimumDeliveryMessage() {
                if (this.complete) return false;
                if (this.orderType !== 'deliver') return false;
                if (this.restaurantSettings.minimum_delivery <= 0) return false;
                return this.total < this.restaurantSettings.minimum_delivery;
            },
            disableSwitch() {
                if (!this.restaurantSettings.minimum_delivery) return false;
                if (this.restaurantSettings.minimum_delivery <= 0) return false;
                return this.total < this.restaurantSettings.minimum_delivery && this.checkout;
            },
        },
        methods: {
            ...mapActions({
                removeFromCart: 'cart/removeFromCart',
                emptyCart: 'cart/emptyCart',
                addOneToCart: 'cart/addOneToCart',
                substractFromCart: 'cart/substractFromCart',
            }),

            ...mapMutations({
                setCheckout: 'cart/setCheckout',
            }),

            productPrice(product) {
                if (product.dish_extras.length > 0) {
                    return (product.dish_extras
                        .map(item => item.price)
                        .reduce((a, b) => a + b, 0)
                        + product.price) * product.quantity;
                }

                return product.price * product.quantity;
            },
        },
    };
</script>

<style lang="scss">
    .cart {
        position: fixed;
        top: 100%;
        left: 0;
        z-index: 100;
        display: flex;
        flex-flow: column;
        width: 100%;
        height: 100%;
        max-height: 100vh;
        padding: 26px;
        background: var(--baseColor);
        transition: top 0.75s ease-in-out;

        &.open {
            top: 0;
        }

        @include respond-to(md) {
            position: sticky;
            top: 36px;
            left: auto;
            display: flex;
            height: 100%;
            max-height: calc(100vh - 72px);
            border-radius: 6px;
        }

        &-wrapper {
            height: 100%;
        }

        &-header {
            .button {
                color: var(--baseColor);
                background: rgb(var(--cartContrast));

                @include respond-to(md) {
                    display: none;
                }

                &:hover {
                    background: rgba(var(--cartContrast), 0.8);
                }
            }
        }

        h2 {
            margin-bottom: 4px;
            font-size: 20px;
            color: rgb(var(--cartContrast));
        }

        &-amount,
        &-empty {
            font-size: 16px;
            color: rgba(var(--cartContrast), 0.4);
        }

        &-product {
            &-list {
                position: relative;
                padding: 0;
                margin: 28px 0 16px;
                overflow: auto;
                list-style: none;

                &::after {
                    position: sticky;
                    bottom: 0;
                    display: block;
                    width: 100%;
                    height: 50px;
                    margin-top: -10px;
                    content: '';
                    background: linear-gradient(rgba(var(--baseColor), 0), var(--baseColor) 60%);
                }
            }

            &-summary {
                flex: 1 0 60%;
                max-width: 60%;
            }

            &-item {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 16px 0;
                font-size: 16px;
                color: rgb(var(--cartContrast));
                border-bottom: 1px solid  rgba(var(--cartContrast), 0.2);
            }

            &-title {
                margin-bottom: 6px;
                font-weight: bold;
            }

            &-qty {
                display: flex;
                align-items: center;
                font-style: italic;
                color: rgba(var(--cartContrast), 0.4);

                .button {
                    width: 20px;
                    height: 20px;
                    margin-left: 4px;
                    font-size: 14px;
                    color: rgb(var(--cartContrast));
                    background: none;
                    border: 1px solid rgba(var(--cartContrast), 0.4);
                }
            }

            &-price {
                font-weight: bold;

                .button {
                    width: 25px;
                    height: 25px;
                    margin-left: 6px;
                    color: rgb(var(--cartContrast));
                    background: none;
                    border: 1px solid rgba(var(--cartContrast), 0.4);
                }
            }
        }

        &-total {
            margin: 26px 0;
            font-size: 16px;

            &-row {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin: 8px 0;
                color: rgb(var(--cartContrast));

                &.last {
                    padding-top: 16px;
                    font-weight: bold;
                    border-top: 1px solid rgba(var(--cartContrast), 0.2);

                    .cart-total-price {
                        font-size: 20px;
                    }
                }
            }

            &-label,
            &-price {
                display: block;
            }
        }

        &-checkout {
            &-button.button {
                width: 100%;
                color: var(--baseColor);
                background: rgb(var(--cartContrast));

                &:hover {
                    background: rgba(var(--cartContrast), 0.8);
                }
            }
        }
    }

    .mini-cart {
        position: fixed;
        bottom: 0;
        left: 0;
        z-index: 80;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: 70px;
        padding: 15px 30px;
        font-size: 18px;
        color: rgb(var(--cartContrast));
        background: var(--baseColor);
        border-radius: 20px 20px 0 0;
        transition: bottom 0.75s ease-in-out;

        &.close {
            bottom: 100%;
        }

        &.gone {
            bottom: -70px;
        }

        .button {
            position: relative;
            width: 40px;
            height: 40px;
            font-size: 20px;
            color: var(--baseColor);
            background: rgb(var(--cartContrast));

            .counter {
                position: absolute;
                top: -4px;
                right: -4px;
                display: flex;
                align-items: center;
                justify-content: center;
                width: 18px;
                height: 18px;
                font-size: 12px;
                font-weight: bold;
                line-height: 1.2;
                color: #fff;
                background: #ff7636;
                border-radius: 50%;
            }

            &:hover {
                background: rgba(var(--cartContrast), 0.8);
            }
        }

        @include respond-to(md) {
            display: none;
        }
    }
</style>
